/* eslint-disable max-len */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { uniqueId } from 'lodash'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { replaceAndStrip } from '../../../utils/string'
import Modal from '../common/Modal'
import AddAppModule from './AddAppModule'
import AddTeamAndMemberModule from './AddTeamAndMemberModule'
import { compile } from '../../../utils/search'
import { IconName } from '@fortawesome/fontawesome-common-types'
import { convertHexColor } from '../../../utils/color'

interface AutoLabeling {
  id: number | null
  services: string[]
  keyword: {
    operator: string | null
    words: string[]
  }
  file_paths: string[]
  url: {
    pattern: string | null
    words: string[]
  }
  teams: {
    id: number
    name: string
  }[]
  members: {
    id: number
    name: string
  }[]
}

interface Tasks {
  id: number | null
  name: string
  color: string
}

interface Item {
  item_type: string,
}

type Items = Item[]

interface ModalState {
  label_group_id: number | null
  label_group_name: string
  label_group_code?: string | null
  label_id?: number | null
  label_name?: string
  label_code?: string | null
  label_color?: string
  label_master_id?: number | null
  auto_labelings?: AutoLabeling[]
  scheduled_start_date?: string // 予定有効期限開始日
  scheduled_end_date?: string // 予定有効期限終了日
  scheduled_time?: number // 想定工数(秒)
  disabled?: boolean
  type: string
}

interface Props {
  modalState: ModalState
  closeCallback: VoidFunction
  addCallback: (params: ModalState) => void
  isDisplay: boolean
  tasks: Tasks[]
  currentTasks: string[]
  teamList: { id: number, name: string, members: { id: number, name: string }[] }[]
  memberList: { id: number, name: string }[]
}

const AddLabelModal: FC<Props> = (props) => {
  const {
    modalState,
    closeCallback,
    addCallback,
    isDisplay,
    tasks,
    currentTasks,
    teamList,
    memberList,
  } = props

  const {
    task,
  } = useSelector((state: RootState) => state)

  const LIMIT_NUMBER = 5

  const SELECT_LIST = [
    {
      value: "team_and_members",
      label: "チーム・メンバー",
      color: "#4472ba",
      icon: "users",
    },
    {
      value: "services",
      label: "アプリケーション",
      color: "#ba5a44",
      icon: "star",
    },
    {
      value: "keywords",
      label: "キーワード",
      color: "#baa744",
      icon: "pen",
    },
    {
      value: "url",
      label: "URL",
      color: "#209a48",
      icon: "window-maximize",
    },
    {
      value: "file_paths",
      label: "ファイルパス",
      color: "#8c30a7",
      icon: "folder",
    },
  ]

  const IMITATION = [
    "Chrome",
    "Edge",
    "Firefox",
  ]

  const autoLabelingConditionInit = {
    id: null,
    services: [],
    keyword: {
      operator: "or",
      words: [],
    },
    file_paths: [],
    url: {
      pattern: "partial",
      words: [],
    },
    teams: [],
    members: [],
  }

  const [labelId, setLabelId] = useState<number | null>(null)
  const [labelMasterId, setLabelMasterId] = useState<number | null>(null)
  const [labelScheduledTime, setLabelScheduledTime] = useState<string>("")
  const [labelCode, setLabelCode] = useState<string>("")

  const [autoLabelingConditions, setAutoLabelingConditions] = useState<AutoLabeling[]>([])

  const [keywordText, setKeywordText] = useState<string>("")
  const [pathText, setPathText] = useState<string>("")
  const [urlText, setUrlText] = useState<string>("")
  const [composition, setComposition] = useState<boolean>(false)

  const [data, setData] = useState<Tasks[]>([])
  const [searchWord, setSearchWord] = useState<string>("")
  const [isDisplayList, setIsDisplayList] = useState<boolean>(false)

  const [selectNumber, setSelectNumber] = useState<number>(0)
  const [limitNumber, setLimitNumber] = useState<number>(1)

  const [items, setItems] = useState<Items[]>([[]])

  const [selectUrlPattern, setSelectUrlPattern] = useState<string | null>("partial")
  const [selectKeywordOperator, setSelectKeywordOperator] = useState<string | null>("or")

  const [isDisplayTypeSelect, setIsDisplayTypeSelect] = useState<boolean>(false)

  const errorMessages = useMemo(() => {
    const errors:string[] = []
    autoLabelingConditions.forEach((v, i) => {
      if (items[i]?.filter((w) => w.item_type === "team_and_members").length && (!v.teams.length && !v.members.length)) {
        errors.push(`条件${i + 1}: チーム・メンバーが設定されていません`)
      }
      if (items[i]?.filter((w) => w.item_type === "services").length && !v.services.length) {
        errors.push(`条件${i + 1}: アプリケーションが設定されていません`)
      }
      if (items[i]?.filter((w) => w.item_type === "services").length && v.services.some((w) => IMITATION.includes(w))) {
        errors.push(`条件${i + 1}: Webブラウザーをアプリケーションとして設定できません`)
      }
      if (items[i]?.filter((w) => w.item_type === "keywords").length && !v.keyword.words.length) {
        errors.push(`条件${i + 1}: キーワードが設定されていません`)
      }
      if (items[i]?.filter((w) => w.item_type === "url").length && !v.url.words.length) {
        errors.push(`条件${i + 1}: URLが設定されていません`)
      }
      if (items[i]?.filter((w) => w.item_type === "file_paths").length && !v.file_paths.length) {
        errors.push(`条件${i + 1}: ファイルパスが設定されていません`)
      }
      if (items[i]?.filter((w) => w.item_type === "url").length && !items[i]?.filter((w) => w.item_type === "services").length) {
        errors.push(`条件${i + 1}: URLを設定する場合、アプリケーションの設定も必須です`)
      }
    })
    return errors
  }, [autoLabelingConditions, items])

  const resetParam = () => {
    setSearchWord("")
    setLabelId(null)
    setLabelMasterId(null)
    setLabelScheduledTime("")
    setLabelCode("")
    setAutoLabelingConditions([])
    setSelectNumber(0)
    setLimitNumber(1)
    setSelectUrlPattern("partial")
    setSelectKeywordOperator("or")
    setIsDisplayTypeSelect(false)
  }

  const closeModal = () => {
    closeCallback()
    resetParam()
  }

  const addLabel = () => {
    const tmpModalState = {
      ...modalState,
      label_id: labelId,
      label_master_id: labelMasterId || task.taskLabelSetting?.label_masters.find((v) => v.name === searchWord)?.id || null,
      label_name: searchWord,
      label_code: labelCode || null,
      label_color: task.taskLabelSetting?.label_masters.find((v) => v.name === searchWord)?.color || "#B2B2B2",
      auto_labelings: autoLabelingConditions.filter((v) => (
        v.services.length || v.keyword.words.length || v.teams.length || v.members.length || v.url.words.length || v.file_paths.length
      )),
      scheduled_time: Number(labelScheduledTime) * 3600,
    }
    addCallback(tmpModalState)
    resetParam()
  }

  const addKeyword = (key: string) => {
    const trimValue = replaceAndStrip(keywordText)
    if (composition || !trimValue || key !== "Enter" || autoLabelingConditions[selectNumber].keyword.words?.includes(trimValue)) return
    setAutoLabelingConditions(
      (prev) => prev.map((v, i) => (i === selectNumber ? { ...v, keyword: { ...v.keyword, words: [...v.keyword.words, trimValue] } } : v)),
    )
    setKeywordText("")
  }

  const delKeyword = (keyword: string) => {
    if (!keyword || !autoLabelingConditions[selectNumber].keyword.words?.includes(keyword)) return
    setAutoLabelingConditions(
      (prev) => prev.map((v, i) => (i === selectNumber ? { ...v, keyword: { ...v.keyword, words: [...v.keyword.words.filter((w) => w !== keyword)] } } : v)),
    )
  }

  const blurKeyword = (keyword: string) => {
    setKeywordText("")
    const trimValue = replaceAndStrip(keyword)
    if (!trimValue || autoLabelingConditions[selectNumber].keyword.words?.includes(trimValue)) return
    setAutoLabelingConditions(
      (prev) => prev.map((v, i) => (i === selectNumber ? { ...v, keyword: { ...v.keyword, words: [...v.keyword.words, trimValue] } } : v)),
    )
  }

  const search = (words: string) => {
    setSearchWord(words)
    setIsDisplayList(true)
    if (!words) {
      setData(tasks)
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const tmpData = tasks.filter((value) => {
      if (value === null) return false
      return (compile(word).test(value.name.toLocaleLowerCase()))
    })
    setData(tmpData)
  }

  const reset = () => {
    setSearchWord("")
    setLabelMasterId(0)
    setData(tasks)
    setIsDisplayList(false)
  }

  const selectMasterLabel = (param: Tasks) => {
    setLabelMasterId(param.id || null)
    setSearchWord(param.name)
    setIsDisplayList(false)
  }

  const addUrl = (key: string) => {
    const trimValue = replaceAndStrip(urlText)
    if (composition || !trimValue || key !== "Enter" || autoLabelingConditions[selectNumber].url.words.includes(trimValue)) return
    setAutoLabelingConditions(
      (prev) => prev.map((v, i) => (i === selectNumber ? { ...v, url: { ...v.url, words: [...v.url.words, trimValue] } } : v)),
    )
    setUrlText("")
  }

  const delUrl = (url: string) => {
    if (!url || !autoLabelingConditions[selectNumber].url.words.includes(url)) return
    setAutoLabelingConditions(
      (prev) => prev.map((v, i) => (i === selectNumber ? { ...v, url: { ...v.url, words: [...v.url.words.filter((w) => w !== url)] } } : v)),
    )
  }

  const blurUrl = (url: string) => {
    setUrlText("")
    const trimValue = replaceAndStrip(url)
    if (!trimValue || autoLabelingConditions[selectNumber].url.words.includes(trimValue)) return
    setAutoLabelingConditions(
      (prev) => prev.map((v, i) => (i === selectNumber ? { ...v, url: { ...v.url, words: [...v.url.words, trimValue] } } : v)),
    )
  }

  const addPath = (key: string) => {
    const trimValue = replaceAndStrip(pathText)
    if (composition || !trimValue || key !== "Enter" || autoLabelingConditions[selectNumber].file_paths?.includes(trimValue)) return
    setAutoLabelingConditions(
      (prev) => prev.map((v, i) => (i === selectNumber ? { ...v, file_paths: [...v.file_paths, trimValue] } : v)),
    )
    setPathText("")
  }

  const delPath = (path: string) => {
    if (!path || !autoLabelingConditions[selectNumber].file_paths?.includes(path)) return
    setAutoLabelingConditions(
      (prev) => prev.map((v, i) => (i === selectNumber ? { ...v, file_paths: [...v.file_paths.filter((w) => w !== path)] } : v)),
    )
  }

  const blurPath = (path: string) => {
    setPathText("")
    const trimValue = replaceAndStrip(path)
    if (!trimValue || autoLabelingConditions[selectNumber].file_paths?.includes(trimValue)) return
    setAutoLabelingConditions(
      (prev) => prev.map((v, i) => (i === selectNumber ? { ...v, file_paths: [...v.file_paths, trimValue] } : v)),
    )
  }

  const addCondition = () => {
    setAutoLabelingConditions(
      (prev) => [
        ...prev,
        { ...autoLabelingConditionInit },
      ],
    )
    setLimitNumber(limitNumber + 1)
    setSelectNumber(limitNumber)
    setItems([...items, []])
  }

  const delCondition = (num: number) => {
    setAutoLabelingConditions((prev) => prev.filter((_v, i) => i !== num))
    setLimitNumber(limitNumber - 1)
    setSelectNumber(0)
    setItems(items.filter((_v, i) => i !== num))
  }

  const addSelectItem = (itemType: string) => {
    setIsDisplayTypeSelect(false)
    setItems((prev) => prev.map((v, i) => (i === selectNumber ? [...v, { item_type: itemType }] : v)))
  }

  const clearCondition = (index: number) => {
    const targetItem = items[selectNumber].find((_v, i) => i === index)
    let deleteCondition = {}
    if (targetItem?.item_type === "team_and_members") {
      deleteCondition = { teams: [], members: [] }
    } else if (targetItem?.item_type === "services") {
      deleteCondition = { services: [] }
    } else if (targetItem?.item_type === "keywords") {
      deleteCondition = { keyword: { pattern: "or", words: [] } }
    } else if (targetItem?.item_type === "url") {
      deleteCondition = { url: { pattern: "partial", words: [] } }
    } else if (targetItem?.item_type === "file_paths") {
      deleteCondition = { file_paths: [] }
    }
    setAutoLabelingConditions((prev) => prev.map((v, i) => (i === selectNumber ? { ...v, ...deleteCondition } : v)))
  }

  const deleteItem = (index: number) => {
    clearCondition(index)
    const otherItems = items[selectNumber].filter((_v, i) => i !== index)
    setItems((prev) => prev.map((v, i) => (i === selectNumber ? otherItems : v)))
  }

  const changeUrlPattern = (pattern: string) => {
    setAutoLabelingConditions((prev) => prev.map((v, i) => (i === selectNumber ? { ...v, url: { ...v.url, pattern: pattern } } : v)))
  }

  const changeKeywordOperator = (operator: string) => {
    setAutoLabelingConditions((prev) => prev.map((v, i) => (i === selectNumber ? { ...v, keyword: { ...v.keyword, operator: operator } } : v)))
  }

  const buildItemDetail = (type: string) => (
    <div
      style={{
        border: "solid 1px #CCCCCC",
        borderRadius: "4px",
        backgroundColor: "#FFFFFF",
        padding: "8px",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "4px",
      }}
    >
      {
        type === "team_and_members" && (
          <>
            {
              autoLabelingConditions[selectNumber]?.teams?.map((value) => (
                <div
                  style={{
                    backgroundColor: "#dddddd",
                    borderRadius: "20px",
                    padding: "4px 8px",
                    display: "inline-block",
                  }}
                  key={`condition_${value.id}`}
                >
                  {value.name}
                  <span
                    style={{
                      marginLeft: "4px",
                      cursor: "pointer",
                    }}
                    onClick={() => setAutoLabelingConditions(
                      (prev) => prev.map((v, i) => (i === selectNumber ? { ...v, teams: v.teams.filter((w) => w.id !== value.id) } : v)),
                    )}
                    role="none"
                  >
                    <FontAwesomeIcon icon={['fas', 'times']} />
                  </span>
                </div>
              ))
            }
            {
              autoLabelingConditions[selectNumber]?.members?.map((value) => (
                <div
                  style={{
                    backgroundColor: "#dddddd",
                    borderRadius: "20px",
                    padding: "4px 8px",
                    display: "inline-block",
                  }}
                  key={`condition_${value.id}`}
                >
                  {value.name}
                  <span
                    style={{
                      marginLeft: "4px",
                      cursor: "pointer",
                    }}
                    onClick={() => setAutoLabelingConditions(
                      (prev) => prev.map((v, i) => (
                        (i === selectNumber ? { ...v, members: v.members.filter((w) => w.id !== value.id) } : v)
                      )),
                    )}
                    role="none"
                  >
                    <FontAwesomeIcon icon={['fas', 'times']} />
                  </span>
                </div>
              ))
            }
            <div
              style={{
                width: "100%",
              }}
            >
              <AddTeamAndMemberModule
                teamIdList={autoLabelingConditions[selectNumber]?.teams?.map((v) => v.id) || []}
                callbackTeams={(teams) => setAutoLabelingConditions(
                  (prev) => prev.map((v, i) => (
                    (i === selectNumber ? { ...v, teams: teams } : v)
                  )),
                )}
                teamList={teamList.map((v) => ({ ...v, origin_name: v.name }))}
                memberIdList={autoLabelingConditions[selectNumber]?.members?.map((v) => v.id) || []}
                callbackMembers={(members) => setAutoLabelingConditions(
                  (prev) => prev.map((v, i) => (
                    (i === selectNumber ? { ...v, members: members } : v)
                  )),
                )}
                memberList={
                  [
                    ...memberList,
                    ...teamList.map((v) => v.members).flat(),
                  ].filter((v, i, s) => s.findIndex((e) => e.id === v.id) === i)
                }
              />
            </div>
          </>
        )
      }
      {
        type === "services" && (
          <>
            {
              autoLabelingConditions[selectNumber]?.services?.map((value) => (
                <div
                  style={{
                    backgroundColor: IMITATION.includes(value) ? "#ffd2d2" : "#dddddd",
                    borderRadius: "20px",
                    padding: "4px 8px",
                    display: "inline-block",
                  }}
                  key={`condition_${value}`}
                >
                  {value}
                  <span
                    style={{
                      marginLeft: "4px",
                      cursor: "pointer",
                    }}
                    onClick={() => setAutoLabelingConditions(
                      (prev) => prev.map((v, i) => (i === selectNumber ? { ...v, services: v.services.filter((w) => w !== value) } : v)),
                    )}
                    role="none"
                  >
                    <FontAwesomeIcon icon={['fas', 'times']} />
                  </span>
                </div>
              ))
            }
            <AddAppModule
              appList={autoLabelingConditions[selectNumber]?.services || []}
              callbackApps={(apps) => setAutoLabelingConditions(
                (prev) => prev.map((v, i) => (
                  i === selectNumber ? { ...v, services: apps } : v
                )),
              )}
            />
          </>
        )
      }
      {
        type === "keywords" && (
          <>
            {
              autoLabelingConditions[selectNumber]?.keyword.words?.map((keyword) => (
                <div
                  style={{
                    backgroundColor: "#dddddd",
                    borderRadius: "20px",
                    padding: "4px 8px",
                    display: "inline-block",
                  }}
                  key={uniqueId()}
                >
                  {keyword}
                  <span
                    style={{
                      marginLeft: "4px",
                      cursor: "pointer",
                    }}
                    onClick={() => delKeyword(keyword)}
                    role="none"
                  >
                    <FontAwesomeIcon icon={['fas', 'times']} />
                  </span>
                </div>
              ))
            }
            <input
              className="keyword_input"
              type="text"
              value={keywordText}
              onChange={(e) => setKeywordText(e.target.value)}
              onKeyDown={(e) => addKeyword(e.key)}
              onBlur={(e) => blurKeyword(e.target.value)}
              onCompositionStart={() => setComposition(true)}
              onCompositionEnd={() => setComposition(false)}
              placeholder={autoLabelingConditions[selectNumber]?.keyword.words.length ? "" : "キーワードを入力してエンターキーを押してください"}
            />
          </>
        )
      }
      {
        type === "url" && (
          <>
            {
              autoLabelingConditions[selectNumber]?.url.words?.map((word) => (
                <div
                  style={{
                    backgroundColor: "#dddddd",
                    borderRadius: "20px",
                    padding: "4px 8px",
                    display: "inline-block",
                  }}
                  key={uniqueId()}
                >
                  {word}
                  <span
                    style={{
                      marginLeft: "4px",
                      cursor: "pointer",
                    }}
                    onClick={() => delUrl(word)}
                    role="none"
                  >
                    <FontAwesomeIcon icon={['fas', 'times']} />
                  </span>
                </div>
              ))
            }
            <input
              className="keyword_input"
              type="text"
              value={urlText}
              onChange={(e) => setUrlText(e.target.value)}
              onKeyDown={(e) => addUrl(e.key)}
              onBlur={(e) => blurUrl(e.target.value)}
              onCompositionStart={() => setComposition(true)}
              onCompositionEnd={() => setComposition(false)}
              placeholder={autoLabelingConditions[selectNumber]?.url.words.length ? "" : "URLを入力してエンターキーを押してください"}
            />
          </>
        )
      }
      {
        type === "file_paths" && (
          <>
            {
              autoLabelingConditions[selectNumber]?.file_paths?.map((path) => (
                <div
                  style={{
                    backgroundColor: "#dddddd",
                    borderRadius: "20px",
                    padding: "4px 8px",
                    display: "inline-block",
                  }}
                  key={uniqueId()}
                >
                  {path}
                  <span
                    style={{
                      marginLeft: "4px",
                      cursor: "pointer",
                    }}
                    onClick={() => delPath(path)}
                    role="none"
                  >
                    <FontAwesomeIcon icon={['fas', 'times']} />
                  </span>
                </div>
              ))
            }
            <input
              className="keyword_input"
              type="text"
              value={pathText}
              onChange={(e) => setPathText(e.target.value)}
              onKeyDown={(e) => addPath(e.key)}
              onBlur={(e) => blurPath(e.target.value)}
              onCompositionStart={() => setComposition(true)}
              onCompositionEnd={() => setComposition(false)}
              placeholder={autoLabelingConditions[selectNumber]?.file_paths.length ? "" : "ファイルパス(例:¥¥192.168.1.1¥user¥など)を入力してエンターキーを押してください"}
            />
          </>
        )
      }
    </div>
  )

  const buildText = (type: string) => (
    <div
      style={{
        display: "flex",
        flexFlow: "column",
        gap: "4px",
        color: "#666666",
      }}
    >
      {
        type === "team_and_members" && (
          <>
            <div
              style={{
                padding: "0 8px",
              }}
            >
              {`※ 指定チーム・メンバー${(autoLabelingConditions[selectNumber]?.teams.length + autoLabelingConditions[selectNumber]?.members.length) > 1 ? "のいずれか" : ""}が含まれている場合、自動仕分けされます。`}
            </div>
          </>
        )
      }
      {
        type === "services" && (
          <>
            <div
              style={{
                padding: "0 8px",
              }}
            >
              {`※ 指定アプリケーション${autoLabelingConditions[selectNumber]?.services.length > 1 ? "のいずれか" : ""}が含まれている場合、自動仕分けされます。`}
            </div>
          </>
        )
      }
      {
        type === "keywords" && (
          <>
            <div
              style={{
                padding: "0 8px",
              }}
            >
              {`※ 指定キーワードの${selectKeywordOperator === "or" ? "いずれか" : "全て"}が含まれている場合、自動仕分けされます。`}
            </div>
            <div
              style={{
                padding: "0 8px",
              }}
            >
              ※ スペースや記号についても、含んだ状態のまま設定されます。ご注意ください。
            </div>
          </>
        )
      }
      {
        type === "url" && (
          <>
            <div
              style={{
                padding: "0 8px",
              }}
            >
              {`※ 指定URL${autoLabelingConditions[selectNumber]?.url.words.length > 1 ? "のいずれか" : ""}${selectUrlPattern === "exact" ? "と一致している" : "が含まれている"}場合、自動仕分けされます。`}
            </div>
            <div
              style={{
                padding: "0 8px",
              }}
            >
              ※ スペースや記号についても、含んだ状態のまま設定されます。ご注意ください。
            </div>
            <div
              style={{
                padding: "0 8px",
                color: "#FF0000",
              }}
            >
              ※ アプリケーションの自動仕分け条件設定が必須になります。併せてご設定ください。
            </div>
          </>
        )
      }
      {
        type === "file_paths" && (
          <>
            <div
              style={{
                padding: "0 8px",
              }}
            >
              {`※ 指定ファイルパス${autoLabelingConditions[selectNumber]?.file_paths.length > 1 ? "のいずれか" : ""}が含まれている場合、自動仕分けされます。`}
            </div>
            <div
              style={{
                padding: "0 8px",
              }}
            >
              ※ スペースや記号についても、含んだ状態のまま設定されます。ご注意ください。
            </div>
          </>
        )
      }
    </div>
  )

  const calcItems = (indexNum: number, conditions?: AutoLabeling[]) => {
    const labelingConditions = conditions || autoLabelingConditions
    const tmpItems:Item[] = []
    if (!conditions) {
      items[indexNum]?.forEach((v) => {
        if (v.item_type === "team_and_members") {
          tmpItems.push({ item_type: "team_and_members" })
        }
        if (v.item_type === "services") {
          tmpItems.push({ item_type: "services" })
        }
        if (v.item_type === "keywords") {
          tmpItems.push({ item_type: "keywords" })
        }
        if (v.item_type === "url") {
          tmpItems.push({ item_type: "url" })
        }
        if (v.item_type === "file_paths") {
          tmpItems.push({ item_type: "file_paths" })
        }
      })
    } else {
      if (labelingConditions[indexNum]?.teams.length || labelingConditions[indexNum]?.members.length) {
        tmpItems.push({ item_type: "team_and_members" })
      }
      if (labelingConditions[indexNum]?.services.length) {
        tmpItems.push({ item_type: "services" })
      }
      if (labelingConditions[indexNum]?.keyword.words.length) {
        tmpItems.push({ item_type: "keywords" })
      }
      if (labelingConditions[indexNum]?.url.words.length) {
        tmpItems.push({ item_type: "url" })
      }
      if (labelingConditions[indexNum]?.file_paths.length) {
        tmpItems.push({ item_type: "file_paths" })
      }
    }
    return tmpItems
  }

  useEffect(() => {
    if (!isDisplay) return
    setSearchWord(modalState.label_name || "")
    setLabelId(modalState.label_id || null)
    setLabelMasterId(modalState.label_master_id || null)
    setLabelScheduledTime(((modalState.scheduled_time || 0) / 3600).toString() || "")
    setLabelCode(modalState.label_code || "")
    setData(tasks)
    setAutoLabelingConditions(modalState.auto_labelings?.length ? modalState.auto_labelings : [autoLabelingConditionInit])
    setLimitNumber(modalState.auto_labelings?.length ? modalState.auto_labelings?.length : 1)
    setItems(
      modalState.auto_labelings?.length
        ? Array(modalState.auto_labelings.length).fill(null).map((_v, i) => calcItems(i, modalState.auto_labelings))
        : [[]],
    )
  }, [isDisplay])

  useEffect(() => {
    if (!autoLabelingConditions.length) return
    const tmpItems = calcItems(selectNumber)
    setItems([...items.map((v, i) => (i === selectNumber ? tmpItems : v))])
    setSelectUrlPattern(autoLabelingConditions[selectNumber].url.pattern || "partial")
    setSelectKeywordOperator(autoLabelingConditions[selectNumber].keyword.operator || "or")
  }, [selectNumber, autoLabelingConditions])

  return (
    <Modal
      title={modalState.type === "create" ? "業務追加" : "自動仕分け設定"}
      show={isDisplay}
      wrapperClass="label_add_modal"
      closeCallback={closeModal}
      closeable
    >
      <>
        <div
          style={{
            textAlign: "center",
            marginBottom: "24px",
          }}
        >
          <span
            style={{
              fontSize: "20px",
              fontWeight: "bold",
            }}
          >
            {modalState.label_group_name}
          </span>
          &nbsp;
          プロジェクトの業務を
          {modalState.type === "create" ? "追加" : "編集"}
          します
        </div>
        <div className="item">
          <div className="item_header">
            <div className="title">
              業務名
            </div>
          </div>
          <div className="detail row_1">
            {
              modalState.type === "create" ? (
                <>
                  <div className="select_search">
                    <input
                      type="text"
                      onChange={(event) => search(replaceAndStrip(event.target.value))}
                      value={searchWord}
                      onFocus={() => setIsDisplayList(true)}
                      placeholder="業務を検索して選択、もしくは、入力"
                    />
                    {
                      searchWord && (
                        <button type="button" className="reset" onClick={reset}>
                          <FontAwesomeIcon icon={['far', 'times-circle']} />
                        </button>
                      )
                    }
                    {
                      isDisplayList && (
                        <>
                          {
                            data.length ? (
                              <div className="select_list">
                                {
                                  data.map((value) => (
                                    <div
                                      key={uniqueId()}
                                      onClick={() => selectMasterLabel(value)}
                                      role="none"
                                    >
                                      {value.name}
                                    </div>
                                  ))
                                }
                              </div>
                            ) : (
                              <></>
                            )
                          }
                          <div className="close_screen" role="presentation" onClick={() => setIsDisplayList(false)} />
                        </>
                      )
                    }
                  </div>
                  {
                    !searchWord.length && (
                      <div className="error">
                        業務を選択、もしくは、入力してください
                      </div>
                    )
                  }
                  {
                    currentTasks.includes(searchWord) && (
                      <div className="error">
                        この業務名はすでにプロジェクトに設定されています
                      </div>
                    )
                  }
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "50%",
                      backgroundColor: modalState.label_color,
                      marginRight: "4px",
                    }}
                  />
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "bold",
                    }}
                  >
                    {modalState.label_name}
                  </div>
                </div>
              )
            }
          </div>
        </div>
        <div className="item">
          <div className="item_header">
            <div className="title">
              自動仕分け条件
            </div>
            {
              limitNumber < LIMIT_NUMBER && (
                <button
                  type="button"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    backgroundColor: "rgb(213, 232, 212)",
                    color: "rgb(130, 179, 102)",
                    borderRadius: "20px",
                    padding: "4px 8px",
                  }}
                  onClick={addCondition}
                >
                  <FontAwesomeIcon icon={['fas', 'plus']} />
                  条件グループを追加
                </button>
              )
            }
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "0 8px",
              }}
            >
              {
                Array(limitNumber).fill(null).map((_v, i) => (
                  <div
                    key={`tab_${i + 1}`}
                    style={{
                      border: "solid 1px #666666",
                      borderBottom: selectNumber === i ? "solid 1px #FFFFFF" : "solid 1px #666666",
                      borderRadius: "3px 3px 0 0",
                      padding: "8px 16px",
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectNumber(i)}
                    role="none"
                  >
                    {`条件グループ${i + 1}`}
                    {
                      limitNumber > 1 && selectNumber === i && (
                        <span
                          style={{
                            marginLeft: "4px",
                            cursor: "pointer",
                          }}
                          onClick={(e) => {
                            delCondition(i)
                            e.stopPropagation()
                          }}
                          role="none"
                        >
                          <FontAwesomeIcon icon={['fas', 'times']} />
                        </span>
                      )
                    }
                  </div>
                ))
              }
            </div>
          </div>
          <div
            style={{
              border: "solid 1px #666666",
              borderRadius: "4px",
              padding: "0px 16px",
              marginTop: "-1px",
            }}
          >
            <div>
              <div
                style={{
                  padding: "16px 0",
                  display: "flex",
                  flexFlow: "column",
                  gap: "8px",
                }}
              >
                {
                  items[selectNumber]?.map((item, index) => {
                    const targetItem = SELECT_LIST.find((w) => w.value === item.item_type)
                    return (
                      <React.Fragment key={`condition_${selectNumber}_${item.item_type}`}>
                        {
                          !!index && (
                            <div
                              style={{
                                position: "relative",
                                padding: "4px 8px",
                                border: "solid 1px #CCCCCC",
                                borderRadius: "10px",
                                backgroundColor: "#EEEEEE",
                                color: "#999999",
                                width: "fit-content",
                                margin: "-16px auto",
                                zIndex: 1,
                              }}
                            >
                              AND
                            </div>
                          )
                        }
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor: convertHexColor(targetItem?.color || "#E9EEF0", 0.1),
                            padding: "12px 8px",
                          }}
                        >
                          <div
                            style={{
                              width: "100%",
                              display: "flex",
                              flexFlow: "column",
                              gap: "4px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "8px",
                                padding: "0 8px",
                                marginBottom: "4px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontSize: "12px",
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={['fas', targetItem?.icon as IconName]}
                                  style={{
                                    color: targetItem?.color,
                                    fontSize: "14px",
                                  }}
                                />
                                {targetItem?.label}
                              </div>
                              {
                                item.item_type === "keywords" && (
                                  <div
                                    style={{
                                      width: "fit-content",
                                      position: "relative",
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <select
                                      key={`pattern_keyword_${selectKeywordOperator}`}
                                      onChange={(e) => changeKeywordOperator(e.currentTarget.value)}
                                      defaultValue={selectKeywordOperator || "or"}
                                      style={{
                                        padding: "8px 24px 8px 16px",
                                      }}
                                    >
                                      <option
                                        value="or"
                                      >
                                        いずれかを含む
                                      </option>
                                      <option
                                        value="and"
                                      >
                                        全てを含む
                                      </option>
                                    </select>
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: "50%",
                                        right: "8px",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                      }}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'caret-down']} />
                                    </div>
                                  </div>
                                )
                              }
                              {
                                item.item_type === "url" && (
                                  <div
                                    style={{
                                      width: "fit-content",
                                      position: "relative",
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      backgroundColor: "#FFFFFF",
                                    }}
                                  >
                                    <select
                                      key={`pattern_${selectUrlPattern}`}
                                      onChange={(e) => changeUrlPattern(e.currentTarget.value)}
                                      defaultValue={selectUrlPattern || "partial"}
                                      style={{
                                        padding: "8px 24px 8px 16px",
                                      }}
                                    >
                                      <option
                                        value="partial"
                                      >
                                        部分一致
                                      </option>
                                      <option
                                        value="exact"
                                      >
                                        完全一致
                                      </option>
                                    </select>
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: "50%",
                                        right: "8px",
                                        transform: "translateY(-50%)",
                                        pointerEvents: "none",
                                      }}
                                    >
                                      <FontAwesomeIcon icon={['fas', 'caret-down']} />
                                    </div>
                                  </div>
                                )
                              }
                            </div>
                            <div>
                              {buildItemDetail(item.item_type)}
                            </div>
                            <div>
                              {buildText(item.item_type)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteItem(index)}
                            style={{
                              color: "#FF0000",
                            }}
                          >
                            <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                          </button>
                        </div>
                      </React.Fragment>
                    )
                  })
                }
                {
                  (items[selectNumber]?.map((w) => w.item_type).length || 0) < SELECT_LIST.length && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setIsDisplayTypeSelect(true)}
                          style={{
                            width: "50%",
                            textAlign: "center",
                            border: "solid 1px #4472ba",
                            borderRadius: "4px",
                            backgroundColor: "#eaf2ff",
                            color: "#4472ba",
                          }}
                        >
                          <FontAwesomeIcon icon={['fas', 'plus']} style={{ marginRight: '4px' }} fixedWidth />
                          条件を追加
                        </button>
                      </div>
                      <Modal
                        show={isDisplayTypeSelect}
                        wrapperClass="label_add_modal"
                        closeCallback={() => setIsDisplayTypeSelect(false)}
                        closeable
                      >
                        <>
                          <div
                            style={{
                              fontSize: "20px",
                              marginBottom: "16px",
                            }}
                          >
                            自動仕分け条件の追加
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "left",
                              flexWrap: "wrap",
                              gap: "8px",
                            }}
                          >
                            {
                              SELECT_LIST.map((v) => (
                                <div
                                  onClick={() => !items[selectNumber]?.find((w) => w.item_type === v.value) && addSelectItem(v.value)}
                                  role="none"
                                  style={{
                                    width: "calc((100% / 3) - 8px)",
                                    backgroundColor: items[selectNumber]?.find((w) => w.item_type === v.value) ? "#CCCCCC" : v.color,
                                    color: "#FFFFFF",
                                    borderRadius: "4px",
                                    padding: "24px 8px",
                                    display: "flex",
                                    gap: "4px",
                                    flexFlow: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    cursor: items[selectNumber]?.find((w) => w.item_type === v.value) ? "not-allowed" : "pointer",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "40px",
                                    }}
                                  >
                                    <FontAwesomeIcon icon={['fas', v.icon as IconName]} />
                                  </div>
                                  <div
                                    style={{
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {v.label}
                                  </div>
                                </div>
                              ))
                            }
                          </div>
                        </>
                      </Modal>
                    </>
                  )
                }
              </div>
            </div>
          </div>
          {
            !!errorMessages.length && (
              <div
                style={{
                  border: "solid 1px #FF0000",
                  borderRadius: "4px",
                  padding: "8px",
                  backgroundColor: "#ffe8e8",
                  color: "#FF0000",
                  display: "flex",
                  flexFlow: "column",
                  gap: "4px",
                  marginTop: "16px",
                }}
              >
                {
                  errorMessages.map((v) => (
                    <div
                      key={`${v}`}
                    >
                      {v}
                    </div>
                  ))
                }
              </div>
            )
          }
          {
            autoLabelingConditions[selectNumber]?.services?.some((v) => IMITATION.includes(v)) && (
              <div
                style={{
                  border: "solid 1px #FF0000",
                  borderRadius: "4px",
                  padding: "8px",
                  backgroundColor: "#ffe8e8",
                  color: "#FF0000",
                  display: "flex",
                  flexFlow: "column",
                  gap: "4px",
                  marginTop: "8px",
                  cursor: "pointer",
                }}
                onClick={() => window.open("https://qasee.zendesk.com/hc/ja/requests/new")}
                role="none"
              >
                <div>
                  表示にないアプリケーションは登録することができます。
                </div>
                <div>
                  お手数ですが、
                  <span
                    style={{
                      color: "#4472BA",
                      textDecoration: "underline",
                    }}
                  >
                    カスタマーサポート
                  </span>
                  までご連絡ください。
                </div>
              </div>
            )
          }
        </div>
      </>
      <>
        <button
          type="button"
          className="button cancel"
          onClick={closeModal}
        >
          キャンセル
        </button>
        <button
          key={uniqueId()}
          type="button"
          className="button"
          onClick={addLabel}
          disabled={
            (modalState.type === "create" && (!replaceAndStrip(searchWord) || currentTasks.includes(searchWord)))
            || !!errorMessages.length
          }
        >
          {modalState.type === "create" ? "作成" : "保存"}
        </button>
      </>
    </Modal>
  )
}

export default AddLabelModal
